import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, Car, Mail, Phone, User, FileText, Upload, X, Loader2, CheckCircle, Mic, Square, Play, Pause, Trash2, Gift, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const SERVICES = [
  { value: "full_respray", label: "Full Respray" },
  { value: "partial_respray", label: "Partial Respray" },
  { value: "dent_repair", label: "Dent & Scratch Repair" },
  { value: "bumper_repair", label: "Bumper Repair" },
  { value: "rust_repair", label: "Rust Repair" },
  { value: "custom_paint", label: "Custom Paint Job" },
  { value: "headlight_restoration", label: "Headlight Restoration" },
  { value: "mobile_inspection", label: "Mobile Quote & Inspection" },
  { value: "other", label: "Other (describe below)" },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

export function QuotePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    service_type: "",
    description: "",
    referral_code: "",
  });
  
  // Referral code validation state
  const [referralValidating, setReferralValidating] = useState(false);
  const [referralValid, setReferralValid] = useState(null); // null, true, or false
  const [referralInfo, setReferralInfo] = useState(null);
  
  // Voice note state
  const [isRecording, setIsRecording] = useState(false);
  const [voiceNote, setVoiceNote] = useState(null); // { blob, url, duration }
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  // Listen for service selection from services section
  useEffect(() => {
    const handleServiceSelect = (e) => {
      if (e.detail) {
        setForm(prev => ({ ...prev, service_type: e.detail }));
      }
    };
    window.addEventListener('selectService', handleServiceSelect);
    return () => window.removeEventListener('selectService', handleServiceSelect);
  }, []);

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const audioUrl = URL.createObjectURL(audioBlob);
        
        // Convert to base64
        const reader = new FileReader();
        reader.onloadend = () => {
          setVoiceNote({
            blob: audioBlob,
            url: audioUrl,
            base64: reader.result,
            duration: recordingTime
          });
        };
        reader.readAsDataURL(audioBlob);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      toast.error("Could not access microphone. Please allow microphone access.");
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };
  
  const playVoiceNote = () => {
    if (voiceNote && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };
  
  const deleteVoiceNote = () => {
    if (voiceNote?.url) {
      URL.revokeObjectURL(voiceNote.url);
    }
    setVoiceNote(null);
    setIsPlaying(false);
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValid(null);
      setReferralInfo(null);
      return;
    }
    
    setReferralValidating(true);
    try {
      const res = await axios.get(`${API}/api/referral/validate/${code}`);
      if (res.data.valid) {
        setReferralValid(true);
        setReferralInfo(res.data);
        toast.success(`Referral code valid! Referred by ${res.data.referrer_name}`);
      } else {
        setReferralValid(false);
        setReferralInfo(null);
      }
    } catch (err) {
      setReferralValid(false);
      setReferralInfo(null);
    } finally {
      setReferralValidating(false);
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    if (photos.length + files.length > 5) {
      toast.error("Maximum 5 photos allowed");
      return;
    }
    
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotos(prev => [...prev, { file, preview: e.target.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const required = ['name', 'email', 'phone', 'vehicle_make', 'vehicle_model', 'service_type'];
    for (const field of required) {
      if (!form[field]) {
        toast.error(`Please fill in ${field.replace('_', ' ')}`);
        return;
      }
    }

    setLoading(true);
    try {
      // Convert photos to base64
      const photoData = photos.map(p => p.preview);
      
      // Combine vehicle fields for backend
      const vehicleModel = [form.vehicle_year, form.vehicle_make, form.vehicle_model]
        .filter(Boolean)
        .join(' ')
        .trim();
      
      await axios.post(`${API}/api/quotes`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        vehicle_model: vehicleModel || form.vehicle_model,
        service_type: form.service_type,
        description: form.description,
        photos: photoData,
        voice_note: voiceNote?.base64 || null,
        referral_code: referralValid ? form.referral_code.toUpperCase() : null,
      });
      
      setSubmitted(true);
      toast.success("Quote request submitted!");
    } catch (err) {
      console.error("Quote submission error:", err);
      toast.error(err.response?.data?.detail || "Failed to submit quote. Please try calling us instead.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
        <motion.div {...fadeUp} className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#22c55e]/10 border border-[#22c55e]/30 flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} className="text-[#22c55e]" />
          </div>
          <h1 className="font-heading text-3xl uppercase tracking-tight text-white mb-4">Quote Submitted</h1>
          <p className="text-zinc-400 font-body mb-8">
            Thanks! We'll review your request and get back to you within 24 hours with a quote.
          </p>
          <button
            onClick={() => navigate("/")}
            className="px-8 py-3 bg-[#3b82f6] text-white font-heading text-sm tracking-[0.15em] uppercase hover:bg-[#3b82f6]/80 transition-colors cursor-pointer border-none"
          >
            Back to Home
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] pt-28 pb-20" data-testid="quote-page">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        {/* Back button */}
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm tracking-[0.15em] uppercase font-body transition-colors bg-transparent border-none cursor-pointer mb-8"
        >
          <ArrowLeft size={16} /> Back to Site
        </button>

        {/* Header */}
        <motion.div {...fadeUp} className="mb-12">
          <h1 className="font-heading text-4xl sm:text-5xl uppercase tracking-tight text-white mb-4">
            Get a Quote
          </h1>
          <p className="text-zinc-400 font-body text-base max-w-lg">
            Tell us about your vehicle and what you need done. We'll get back to you within 24 hours with a detailed quote.
          </p>
        </motion.div>

        {/* Form */}
        <motion.form 
          {...fadeUp}
          transition={{ delay: 0.1 }}
          onSubmit={handleSubmit}
          className="space-y-8"
        >
          {/* Contact Info */}
          <div className="bg-[#09090b] border border-white/5 p-6 md:p-8">
            <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-6">Your Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="relative">
                <User size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="text"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Full Name *"
                  className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors"
                  required
                />
              </div>
              <div className="relative">
                <Phone size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Phone Number *"
                  className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors"
                  required
                />
              </div>
            </div>
            <div className="relative mt-4">
              <Mail size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Email Address *"
                className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors"
                required
              />
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="bg-[#09090b] border border-white/5 p-6 md:p-8">
            <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-6">Vehicle Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <Car size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
                <input
                  type="text"
                  name="vehicle_make"
                  value={form.vehicle_make}
                  onChange={handleChange}
                  placeholder="Make (e.g., Toyota) *"
                  className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors"
                  required
                />
              </div>
              <input
                type="text"
                name="vehicle_model"
                value={form.vehicle_model}
                onChange={handleChange}
                placeholder="Model (e.g., Supra) *"
                className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 text-white font-body placeholder-zinc-600 transition-colors"
                required
              />
              <input
                type="text"
                name="vehicle_year"
                value={form.vehicle_year}
                onChange={handleChange}
                placeholder="Year"
                className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 text-white font-body placeholder-zinc-600 transition-colors"
              />
            </div>
          </div>

          {/* Service Selection */}
          <div className="bg-[#09090b] border border-white/5 p-6 md:p-8">
            <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-6">Service Required</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {SERVICES.map(service => (
                <button
                  key={service.value}
                  type="button"
                  onClick={() => setForm({ ...form, service_type: service.value })}
                  className={`p-4 border text-left transition-colors cursor-pointer ${
                    form.service_type === service.value
                      ? "border-[#3b82f6] bg-[#3b82f6]/10"
                      : "border-white/10 hover:border-white/20"
                  }`}
                >
                  <span className="text-sm text-white font-body">{service.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div className="bg-[#09090b] border border-white/5 p-6 md:p-8">
            <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-6">Description</h3>
            <div className="relative">
              <FileText size={16} className="absolute left-4 top-4 text-zinc-600" />
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                placeholder="Tell us more about what you need (damage details, color preferences, etc.)"
                rows={4}
                className="w-full bg-transparent border border-white/10 focus:border-[#3b82f6]/50 outline-none p-4 pl-11 text-white font-body placeholder-zinc-600 transition-colors resize-none"
              />
            </div>
          </div>

          {/* Referral Code */}
          <div className="bg-[#09090b] border border-white/5 p-6 md:p-8">
            <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-2">Referral Code</h3>
            <p className="text-zinc-600 text-sm font-body mb-6">Got a referral code from a friend? Enter it here (optional)</p>
            <div className="relative">
              <Gift size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-600" />
              <input
                type="text"
                name="referral_code"
                value={form.referral_code}
                onChange={(e) => {
                  const value = e.target.value.toUpperCase();
                  setForm({ ...form, referral_code: value });
                  // Debounce validation
                  if (value.length >= 4) {
                    setTimeout(() => validateReferralCode(value), 500);
                  } else {
                    setReferralValid(null);
                    setReferralInfo(null);
                  }
                }}
                placeholder="e.g., JACK-A1B2"
                className={`w-full bg-transparent border outline-none p-4 pl-11 pr-12 text-white font-mono uppercase placeholder-zinc-600 transition-colors ${
                  referralValid === true ? "border-[#22c55e]/50 focus:border-[#22c55e]" :
                  referralValid === false ? "border-red-500/50 focus:border-red-500" :
                  "border-white/10 focus:border-[#3b82f6]/50"
                }`}
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2">
                {referralValidating && <Loader2 size={16} className="text-zinc-500 animate-spin" />}
                {!referralValidating && referralValid === true && <Check size={16} className="text-[#22c55e]" />}
                {!referralValidating && referralValid === false && <X size={16} className="text-red-500" />}
              </div>
            </div>
            {referralInfo && referralValid && (
              <div className="mt-3 p-3 bg-[#22c55e]/10 border border-[#22c55e]/30">
                <p className="text-sm text-[#22c55e] font-body">
                  <strong>Referred by {referralInfo.referrer_name}</strong>
                  {referralInfo.referrer_vehicle && <span className="text-[#22c55e]/70"> ({referralInfo.referrer_vehicle})</span>}
                </p>
                <p className="text-xs text-zinc-400 mt-1">
                  They'll get a free tank of fuel when your job is complete!
                </p>
              </div>
            )}
            {referralValid === false && form.referral_code.length >= 4 && (
              <p className="mt-2 text-sm text-red-400">Invalid or expired referral code</p>
            )}
          </div>

          {/* Photo Upload */}
          <div className="bg-[#09090b] border border-white/5 p-6 md:p-8">
            <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-2">Photos</h3>
            <p className="text-zinc-600 text-sm font-body mb-6">Upload up to 5 photos of your vehicle/damage (optional)</p>
            
            <div className="flex flex-wrap gap-3">
              {photos.map((photo, index) => (
                <div key={index} className="relative w-24 h-24 border border-white/10">
                  <img src={photo.preview} alt="" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 flex items-center justify-center cursor-pointer border-none"
                  >
                    <X size={12} className="text-white" />
                  </button>
                </div>
              ))}
              
              {photos.length < 5 && (
                <label className="w-24 h-24 border border-dashed border-white/20 flex flex-col items-center justify-center cursor-pointer hover:border-white/40 transition-colors">
                  <Upload size={20} className="text-zinc-600 mb-1" />
                  <span className="text-xs text-zinc-600">Add</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                </label>
              )}
            </div>
          </div>

          {/* Voice Note */}
          <div className="bg-[#09090b] border border-white/5 p-6 md:p-8">
            <h3 className="font-heading text-lg uppercase tracking-wide text-white mb-2">Voice Note</h3>
            <p className="text-zinc-600 text-sm font-body mb-6">Record a voice message describing what you need (optional)</p>
            
            {!voiceNote ? (
              <div className="flex items-center gap-4">
                {!isRecording ? (
                  <button
                    type="button"
                    onClick={startRecording}
                    className="flex items-center gap-3 px-6 py-3 bg-[#f59e0b] text-white font-heading text-sm tracking-[0.1em] uppercase border-none cursor-pointer hover:bg-[#f59e0b]/80 transition-colors"
                  >
                    <Mic size={18} />
                    Start Recording
                  </button>
                ) : (
                  <div className="flex items-center gap-4">
                    <button
                      type="button"
                      onClick={stopRecording}
                      className="flex items-center gap-3 px-6 py-3 bg-red-500 text-white font-heading text-sm tracking-[0.1em] uppercase border-none cursor-pointer hover:bg-red-600 transition-colors animate-pulse"
                    >
                      <Square size={18} />
                      Stop
                    </button>
                    <div className="flex items-center gap-2 text-red-400 font-mono">
                      <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                      {formatTime(recordingTime)}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-4 p-4 bg-white/5 border border-white/10">
                <audio ref={audioRef} src={voiceNote.url} onEnded={() => setIsPlaying(false)} />
                <button
                  type="button"
                  onClick={playVoiceNote}
                  className="w-12 h-12 flex items-center justify-center bg-[#22c55e] text-white border-none cursor-pointer hover:bg-[#22c55e]/80 transition-colors"
                >
                  {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                </button>
                <div className="flex-1">
                  <p className="text-white font-body text-sm">Voice Note</p>
                  <p className="text-zinc-500 font-mono text-xs">{formatTime(voiceNote.duration)} recorded</p>
                </div>
                <button
                  type="button"
                  onClick={deleteVoiceNote}
                  className="w-10 h-10 flex items-center justify-center bg-transparent text-red-400 border border-red-500/30 cursor-pointer hover:bg-red-500/10 transition-colors"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            )}
          </div>

          {/* Submit */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-[#3b82f6] text-white font-heading text-sm tracking-[0.15em] uppercase px-8 py-4 hover:bg-[#3b82f6]/80 transition-colors cursor-pointer border-none disabled:opacity-50"
          >
            {loading ? (
              <>
                <Loader2 size={18} className="animate-spin" />
                Submitting...
              </>
            ) : (
              "Submit Quote Request"
            )}
          </button>
        </motion.form>
      </div>
    </div>
  );
}

export default QuotePage;
